/**
 * PortfolioActions — "Salva portafoglio".
 *
 * The saved portfolios have their own page (`/portfolios`), so this control does
 * one thing: turn the current selection — and, when there is one, the analysis
 * just produced — into a saved portfolio. Overwriting an existing name is
 * confirmed in a dialog (A6), never silently.
 */
import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePortfolios } from "@/hooks/usePortfolios";
import { SavePortfolioDialog } from "@/components/portfolios/SavePortfolioDialog";
import { ConfirmDialog } from "@/components/system";

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

export function PortfolioActions({
  selectedEtfs, weights, disabled, knownEtfs = [], sessionId = null,
  openRequest = false, onOpenRequestHandled, onSaved,
  stacked = false,
}) {
  const { portfolios, maxPortfolios, save } = usePortfolios();
  const [saveOpen, setSaveOpen] = useState(false);
  const [pendingOverwrite, setPendingOverwrite] = useState(null);

  // "Ripeti analisi → salva come nuovo portafoglio" asks for this dialog once
  // the new result is on screen.
  useEffect(() => {
    if (!openRequest) return;
    setSaveOpen(true);
    onOpenRequestHandled?.();
  }, [openRequest, onOpenRequestHandled]);

  const items = selectedEtfs.map((e) => ({
    isin: e.isin,
    ticker: e.ticker || "",
    etf_name: e.etf_name || "",
    weight_pct: Number(weights[e.isin] ?? 0),
  }));

  const handleSave = async (name) => {
    try {
      const res = await save({ name, items, sessionId });
      onSaved?.(res?.portfolio, Boolean(res?.analysis_saved));
      toast.success(
        res?.analysis_saved
          ? `Portafoglio "${name}" salvato con l'analisi`
          : `Portafoglio "${name}" salvato`,
      );
      return true;
    } catch (err) {
      if (err?.response?.status === 409) {
        setPendingOverwrite(name);
        return false;
      }
      toast.error(apiError(err, "Salvataggio non riuscito"));
      return false;
    }
  };

  const confirmOverwrite = async () => {
    const name = pendingOverwrite;
    const res = await save({ name, items, overwrite: true, sessionId });
    onSaved?.(res?.portfolio, Boolean(res?.analysis_saved));
    toast.success(`Portafoglio "${name}" aggiornato`);
    setSaveOpen(false);
  };

  const limitReached = portfolios.length >= maxPortfolios;
  const savePortfolioBlockedReason = disabled
    ? "Attendi la fine dell'analisi per salvare."
    : selectedEtfs.length === 0
      ? "Seleziona almeno un ETF per salvare un portafoglio."
      : limitReached
        ? `Hai raggiunto il limite di ${maxPortfolios} portafogli: eliminane uno da "I miei portafogli".`
        : null;

  return (
    <div className={stacked ? "space-y-1.5" : "flex flex-wrap items-center gap-2"}>
      <div className={stacked ? "" : "flex items-center gap-2"}>
        <Button
          data-testid="save-portfolio-btn"
          variant="outline"
          size="sm"
          disabled={Boolean(savePortfolioBlockedReason)}
          onClick={() => setSaveOpen(true)}
          className={`h-10 border-line text-content-secondary hover:text-content
                      hover:border-line-strong transition-colors duration-fast ease-system
                      ${stacked ? "w-full justify-start" : ""}`}
        >
          <Save className="w-4 h-4 mr-2" aria-hidden="true" />
          Salva portafoglio
        </Button>
      </div>
      {savePortfolioBlockedReason && (
        <p data-testid="save-portfolio-reason" className="text-label text-content-muted">
          {savePortfolioBlockedReason}
        </p>
      )}

      <ConfirmDialog
        open={Boolean(pendingOverwrite)}
        onOpenChange={(v) => !v && setPendingOverwrite(null)}
        title={pendingOverwrite ? `Sovrascrivere "${pendingOverwrite}"?` : ""}
        description="Esiste già un portafoglio con questo nome."
        consequence="La selezione, i pesi e l'analisi salvata in precedenza vengono sostituiti da quelli attuali."
        confirmLabel="Sovrascrivi"
        variant="destructive"
        testId="overwrite-portfolio-confirm"
        onConfirm={confirmOverwrite}
      />

      <SavePortfolioDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        etfCount={selectedEtfs.length}
        existingNames={portfolios.map((p) => p.name)}
        hasAnalysis={Boolean(sessionId)}
        onSave={handleSave}
      />
    </div>
  );
}

export default PortfolioActions;
